import React, { useCallback, useEffect, useRef } from "react";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { Button } from "web/react/emo/button";
import { Text } from "web/react/emo/text";
import { useIntersectionObserver } from "web/react/hooks/use-intersection-observer/use-intersection-observer";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import { RetailerPromotionsSerializer } from "web/types/serializers";
import * as styles from "./promotion-summary.css";

export interface PromotionSummaryProps {
    promotions: RetailerPromotionsSerializer;
    title: string;
    onClick: () => void;
    retailerNetwork: string | null;
}

export function PromotionSummary({
    promotions,
    title,
    onClick,
    retailerNetwork,
}: PromotionSummaryProps): React.ReactElement {
    const { active_promotions } = promotions;
    const promotionsCouponRef = useRef<HTMLDivElement | null>(null);
    const [isVisible] = useIntersectionObserver(
        {
            rootMargin: "0px",
            threshold: 1,
            once: true,
        },
        promotionsCouponRef
    );

    useEffect(() => {
        if (isVisible) {
            analytics.event({
                category: "promotion_module_see_all",
                action: "impression",
                nonInteraction: true,
                itemData: { retailer_network: retailerNetwork },
            });
        }
    }, [isVisible, title, retailerNetwork]);

    const activePromotionsTitle =
        active_promotions?.length === 1
            ? gettext("promotion_message.promotion_text.single_active_promotion")
            : gettext("promotion_message.promotion_text.active_promotions", {
                  number: active_promotions.length.toString(),
              });

    const handleClick = useCallback(() => {
        onClick();
        analytics.event({
            category: "promotion_module_see_all",
            action: "clicked",
            itemData: { retailer_network: retailerNetwork },
        });
    }, [onClick, retailerNetwork]);

    return (
        <>
            <div
                className={styles.promotionSummaryContainer}
                data-testid="promo-summary"
                ref={promotionsCouponRef}
                onClick={handleClick}
                role="button"
                tabIndex={0}
            >
                <div className={styles.promoSummaryTextWrapper}>
                    <SVGIcon name="tag-fill" className={styles.tagIcon} flip />
                    <Text as="p" textStyle="body-s">
                        {activePromotionsTitle}
                    </Text>
                </div>
                <Button title={gettext("promotion.button.see_all")} size="sm" variant="primary" />
            </div>
        </>
    );
}
